/**
 * `Workflow_Executor.ts` — the `AgentToolExecutorModel` workflow partial.
 *
 * get_workflow / get_workflow_schema / update_workflow / play_workflow_script
 * over the pure-YAML workflow document, plus start_workflow, go_to_workflow_step,
 * control_media and stop_workflow over the LIVE run. Result strings keep the established `ok:` / `error:` conventions the
 * workflow-builder role's expectations are trained on.
 */

import { Ioc } from "@core/ioc/ioc";
import { AgentToolNames } from "@ai/registry/agentToolNames";
import { TOOL_TRIGGER } from "@ai/registry/AIAgentToolRegistryModel";
import type {
  IStepMoveGuardrail,
  StepMoveRequest,
} from "@ai/guardrails/AIGuardrailsModel.StepMove";
import { SCENE_MODEL, type ISceneModel } from "@models/scene/ISceneModel";
import type { Vec3 } from "@models/data/entityData";
import type { IProjectWorkflowModel } from "@models/workflow/IProjectWorkflowModel";
import {
  WorkflowDocument,
  equalsIgnoreCase,
  findPlayableNavigationTarget,
  formatMediaTimecode,
  getPlayableSteps,
  isStepDisabled,
  parseMediaTimecodeSeconds,
  stepOnStartScriptSpeaks,
} from "@models/workflow/WorkflowDocument";
import {
  CURRENT_WORKFLOW_SCHEMA_VERSION,
  getWorkflowSchema,
  listWorkflowSchemaVersions,
  WORKFLOW_SCHEMA_OVERRIDE_FILE_NAME,
} from "@models/workflow/WorkflowSchemaCatalog";
import type { IAIPromptStoreModel } from "@ai/guardrails/IAIPromptStoreModel";
import {
  serializeWorkflowDocument,
  stringifyYamlDocument,
  type WorkflowYamlIssue,
} from "@models/workflow/WorkflowYaml";
import type { WorkflowScriptRunner } from "@models/workflow/WorkflowScriptRunner";
import type {
  IWorkflowRunModel,
  WorkflowMediaCommand,
  WorkflowMediaPlaybackState,
  WorkflowPlaybackTriggerView,
  WorkflowPlaybackView,
} from "@models/workflow/IWorkflowRunModel";
import {
  K_MOVE_NOT_REQUESTED,
  K_NARRATION_NONE,
  K_NARRATION_SCRIPT,
  formatToolSpokenReply,
} from "./toolTurnHandover";
import {
  getBool,
  getNumber,
  getString,
  type IDomainExecutor,
  type ToolArgs,
} from "./AgentToolExecutorModel";

export class WorkflowExecutor implements IDomainExecutor {
  /**
   * `m_stepMoves` is the guardrail every agent step move passes (AI-1412). The
   * orchestrator always supplies it; a headless harness that builds this
   * executor without one gets moves with no word check.
   */
  constructor(
    private readonly m_workflowModel: IProjectWorkflowModel,
    private readonly m_scriptRunner: WorkflowScriptRunner,
    private readonly m_prompts: IAIPromptStoreModel,
    private readonly m_runModel: IWorkflowRunModel,
    private readonly m_stepMoves: IStepMoveGuardrail | null = null,
  ) {}

  async tryExecute(toolName: string, args: ToolArgs, triggeredBy?: string): Promise<string | null> {
    // Reference material about the FORMAT — it reads no document, so it takes
    // neither the editor flush below nor a loaded workflow. Answering it for a
    // project that has no workflow yet is the point: it is the shape the agent
    // authors the first one from. (It stays project-GATED like its siblings —
    // a workflow only exists inside a project.)
    if (toolName === AgentToolNames.GetWorkflowSchema) {
      return this.readWorkflowSchema(args);
    }

    // Navigation acts on the RUN, which lives in memory and holds the document
    // the run started on — so it takes no editor flush either. Flushing here
    // would also mean a step rename mid-run could move the target under it.
    if (toolName === AgentToolNames.GoToWorkflowStep) {
      // Only the agent's own moves are checked against what the learner said.
      // An omitted caller is the agent's (the executor model's default), so a
      // new caller that forgets to say who it is is checked rather than waved
      // through.
      const checked = (triggeredBy ?? TOOL_TRIGGER.AI) === TOOL_TRIGGER.AI;
      return this.goToWorkflowStepAsync(args, checked);
    }

    // Acts on the live media ELEMENT of the running step. No document, so no
    // editor flush: a flush here would make a pause wait on an S3 read.
    if (toolName === AgentToolNames.ControlMedia) {
      return this.controlMedia(args);
    }

    // Shows or hides the outline PANEL. No document and no run: the outline is
    // the workflow editor's panel as much as the run's, and the flag behind it
    // is one boolean in the view model.
    if (toolName === AgentToolNames.ToggleWorkflowOutline) {
      return this.toggleWorkflowOutline(args);
    }

    // Ends the RUN, so it takes no editor flush either — and no document: the
    // run holds the one it started on, and a stop that had to wait on an S3
    // read would be a stop that could fail.
    if (toolName === AgentToolNames.StopWorkflow) {
      return this.stopWorkflow();
    }

    // STARTS the run — and unlike its twin above it does take the editor
    // flush, inside the method: a preview has to play what the author is
    // LOOKING at, and a start landing in the editor's save debounce would
    // otherwise play the text from before their last keystroke.
    if (toolName === AgentToolNames.StartWorkflow) {
      return this.startWorkflowAsync();
    }

    // Reads the live CAMERA, not the document — no flush, and no loaded
    // workflow needed: the pose is what a step_view is authored FROM, so
    // answering it before the workflow exists is the point.
    if (toolName === AgentToolNames.GetCameraView) {
      return this.getCameraView();
    }

    switch (toolName) {
      case AgentToolNames.GetWorkflow:
      case AgentToolNames.UpdateWorkflow:
      case AgentToolNames.PlayWorkflowScript:
        // The builder must read (and patch over) the text the user actually
        // sees: the Workflow editor debounces its saves, so a tool call
        // landing inside that window would otherwise read a pre-keystroke
        // snapshot — and the stale debounced save would then stomp the edit.
        // (The retired panel prompt bar flushed at send; the main-agent input
        // has no workflow-aware send path, so the flush lives here.)
        await this.m_workflowModel.flushPendingEditsAsync();
        break;
      default:
        return null;
    }

    switch (toolName) {
      case AgentToolNames.GetWorkflow:
        return this.getWorkflowAsync();
      case AgentToolNames.UpdateWorkflow:
        return this.updateWorkflowAsync(args);
      default:
        return this.playWorkflowScriptAsync(args);
    }
  }

  /**
   * The live camera as a `step_view` block, ready to paste into an
   * `update_workflow` patch.
   *
   * Goes through `ISceneModel.captureSceneView()` — the same capture the
   * inspector's "Set Scene View" button uses — so the pose is in the space a
   * step view is stored in (world, Unity axes, metres, scene-relative) with no
   * conversion here. `isOrthographic` rides that value and is dropped: the web
   * camera is perspective-only and the workflow format carries no such flag.
   *
   * Two guards, and the missing third is deliberate: the PROJECT gate is the
   * registry's, since this tool is project-gated like every other workflow tool
   * and is simply not offered without one. What is left is the camera — asked
   * as whether a viewport has ATTACHED one rather than whether the model exists,
   * because `ISceneModel` is registered unconditionally at bootstrap and testing
   * for it would be a tautology that hands the agent a pose it does not have —
   * and an active scene, which `captureSceneView` also requires.
   *
   * Rounded to millimetres. The numbers describe a camera metres from what it
   * is looking at, so the float tail is noise in a payload the agent copies by
   * hand, and rounding is idempotent — a captured view re-read from the file
   * comes back unchanged.
   */
  private getCameraView(): string {
    const sceneModel = Ioc.resolve<ISceneModel>(SCENE_MODEL);
    if (!sceneModel.isSceneViewCameraAttached) {
      return "tool_error: camera_unavailable. The 3D viewport is not active, so there is no camera to read.";
    }
    if (!sceneModel.activeScene) return "error: no active scene";

    const view = sceneModel.captureSceneView();
    if (!view) return "tool_error: camera_unavailable. The camera pose could not be read.";

    return [
      "ok: camera_view",
      "step_view:",
      `  position: ${formatVec3(view.position)}`,
      `  pivot: ${formatVec3(view.pivot)}`,
    ].join("\n");
  }

  private async getWorkflowAsync(): Promise<string> {
    let document: WorkflowDocument | null;
    try {
      document = await this.m_workflowModel.getWorkflowAsync();
    } catch (e) {
      console.warn("[AI: AgentToolExecutor] GetWorkflow failed:", e);
      return "error: workflow_load_failed";
    }

    if (!document) {
      const loadError = this.m_workflowModel.loadError;
      if (loadError) {
        const issues = this.m_workflowModel.loadIssues;
        return [
          `ok: workflow_loaded_unreadable issues=${issues.length} repair=full_yaml`,
          "",
          "YAML validation: FAILED",
          ...(issues.length > 0
            ? issues.map((issue, index) => formatWorkflowYamlIssue(issue, index))
            : [`1. ${loadError}`]),
          "",
          "Repair instructions: the exact stored file is included below. Preserve all valid authored " +
            "text, scripts, metadata, and ordering; fix only the validator issues. Then call " +
            "update_workflow with the ENTIRE corrected YAML file. In this unreadable state, update_workflow " +
            "treats its yaml argument as a full-file repair and replaces these broken bytes. Do not start " +
            "the workflow over unless the user explicitly asks.",
          "",
          "File content (workflow.yaml; raw, preserved verbatim):",
          this.m_workflowModel.unreadableRawText,
        ].join("\n");
      }

      // Not an anomaly: a project has no workflow until someone asks for one
      // (opening the editor no longer seeds a template), so say what to do
      // rather than only what is missing.
      return (
        "error: no_workflow_for_project (this project has no workflow — that is normal, not a failure. " +
        "To create one, call update_workflow with a 'yaml' document following the structure " +
        "get_workflow_schema publishes; the user can also create one from the '+' on the Workflow pill)"
      );
    }

    const report = this.m_workflowModel.validateLoadedWorkflow();
    const scriptCount = document.steps.reduce((sum, step) => sum + step.scripts.length, 0);
    let userState = this.m_runModel.getUserState();
    if (!userState) {
      try {
        userState = await this.m_workflowModel.getWorkflowStateAsync();
      } catch (error) {
        console.warn("[AI: AgentToolExecutor] Workflow user-state load failed:", error);
      }
    }
    // `groups=` appears only when the file actually uses groups, so the ok
    // line stays byte-identical for the (common) ungrouped files.
    const groupCount = document.groups.length;
    // `disabled=` appears only when the file actually parks a step, for the
    // same reason `groups=` does — and it has to appear, because otherwise a
    // parked step is a silent difference between the step count reported here
    // and the one the run and the play panel use.
    const disabledCount = document.steps.filter((step) => isStepDisabled(step)).length;
    const lines = [
      `ok: workflow_loaded playable=${this.m_runModel.canStart ? "yes" : "no"} steps=${document.steps.length}${disabledCount > 0 ? ` disabled=${disabledCount}` : ""}${groupCount > 0 ? ` groups=${groupCount}` : ""} scripts=${scriptCount}`,
      "",
      report.toSummaryText(),
      "",
      "File content (workflow.yaml):",
      serializeWorkflowDocument(document),
    ];
    if (userState) {
      lines.push(
        "",
        "Current user's runtime state (DynamoDB-backed; not editable workflow YAML):",
        stringifyYamlDocument({
          workflow_user_state: {
            run: userState.run,
            current: userState.current,
            current_group: userState.currentGroup,
            current_index: userState.currentIndex,
            step_count: userState.stepCount,
            completed: [...userState.completed],
            visited: [...userState.visited],
            visit_stack: [...userState.visitStack],
            resume_point: userState.resumePoint,
            next: userState.next,
          },
        }).trimEnd(),
      );
    }
    return lines.join("\n");
  }

  private readWorkflowSchema(args: ToolArgs): string {
    const requested = getString(args, "version")?.trim() ?? "";
    const schema = getWorkflowSchema(requested, {
      currentTextOverride: this.m_prompts.getPromptText(
        "workflow",
        WORKFLOW_SCHEMA_OVERRIDE_FILE_NAME,
      ),
    });
    const available = listWorkflowSchemaVersions().join(", ");

    if (schema === null) {
      return (
        `error: unknown_schema_version "${requested}" (available: ${available}; ` +
        "omit 'version' or pass 'latest' for the current format)"
      );
    }

    // The ok line says whether this IS the current format, rather than leaving
    // the agent to compare version strings: an older schema describes a format
    // this build cannot load, and acting on it as if it were live would produce
    // a document every write path then refuses.
    return [
      `ok: workflow_schema version=${schema.version}${schema.isCurrent ? " (current)" : ` (superseded by ${CURRENT_WORKFLOW_SCHEMA_VERSION} — this client reads ${CURRENT_WORKFLOW_SCHEMA_VERSION} only)`} available=${available}`,
      "",
      schema.text,
    ].join("\n");
  }

  private async updateWorkflowAsync(args: ToolArgs): Promise<string> {
    const yaml = getString(args, "yaml");
    if (!yaml?.trim()) {
      return "error: nothing_to_update (provide a yaml patch)";
    }

    try {
      // Ensure unreadable-vs-absent is known before choosing the write path.
      // A broken file is repaired as a FULL document so the builder can
      // remove an invalid key without reconstructing the workflow from a
      // sparse patch. A genuinely absent file still uses patch creation.
      const current = await this.m_workflowModel.getWorkflowAsync();
      const repairingUnreadable = current === null && this.m_workflowModel.loadError !== null;
      if (repairingUnreadable) {
        await this.m_workflowModel.saveYamlTextAsync(yaml, "builder");
      } else {
        await this.m_workflowModel.applyYamlAsync(yaml, "builder");
      }

      const report = this.m_workflowModel.validateLoadedWorkflow();
      return [
        `ok: workflow_updated ${repairingUnreadable ? "yaml_repair" : "yaml_patch"} playable=${this.m_runModel.canStart ? "yes" : "no"}`,
        report.toSummaryText(),
      ].join("\n");
    } catch (e) {
      console.warn("[AI: AgentToolExecutor] UpdateWorkflow failed:", e);
      const message = e instanceof Error ? e.message : String(e);
      // The YAML shape errors name the exact key/line at fault — pass them
      // through so the model can fix its own patch instead of retrying blind.
      if (message.startsWith("invalid_workflow_yaml")) {
        return `error: ${message}`;
      }
      if (message.includes("without an active project") || message.includes("No workflow is loaded")) {
        return "error: no_workflow_for_project";
      }
      return "error: workflow_update_failed";
    }
  }

  private async playWorkflowScriptAsync(args: ToolArgs): Promise<string> {
    // A script belongs to its STEP, and while a run is playing only the step
    // plays it (user direction, 2026-09-10: "The script tool can only be called
    // from within a step"). Played from here it runs outside that step's
    // arrival — the video is not reset, the Next gate is not re-armed, and the
    // step's own order is lost — which is why "show me that again" played the
    // clip from wherever it was. The Guide no longer holds this tool; this is
    // the belt for any role that still does, and the reply names the one call
    // that does the job properly.
    if (this.m_runModel.isRunning) {
      return (
        "error: workflow_running (a step's scripts only play as part of the step while a run is " +
        "playing. To play the step the learner is on again, call go_to_workflow_step with " +
        "direction=replay - video and script included; to show another step, name it with step=. " +
        "control_media replay plays just the step's video again.)"
      );
    }

    let document: WorkflowDocument | null;
    try {
      document = await this.m_workflowModel.getWorkflowAsync();
    } catch {
      return "error: workflow_load_failed";
    }
    if (!document) {
      return "error: no_workflow_for_project";
    }

    const stepName = getString(args, "step");
    if (!stepName?.trim()) {
      return "error: step_required";
    }

    const step = document.findStep(stepName.trim());
    if (!step) {
      const known = document.steps.map((t) => `"${t.name}"`).join(", ");
      return `error: unknown_step "${stepName.trim()}" (steps: ${known})`;
    }

    // A PARKED step's script still plays here, deliberately: this path is now
    // authoring-only (a run is refused above), and previewing what it just
    // wrote is exactly what an author parks a step to keep doing.

    const scriptId = getString(args, "script_id");
    const trigger = getString(args, "trigger");
    const script = scriptId?.trim()
      ? step.findScriptById(scriptId)
      : step.findScriptByTrigger(trigger?.trim() ? trigger : "start");

    if (!script) {
      const available =
        step.scripts.length === 0
          ? "none prepared yet - write them into the step's scripts list via update_workflow"
          : step.scripts.map((s) => `"${s.id}" (${s.trigger})`).join(", ");
      return `error: script_not_found (step "${step.name}" scripts: ${available})`;
    }

    return await this.m_scriptRunner.playScriptAsync(document, step, script);
  }

  /**
   * `control_media` — replay, skip, play or pause the running step's video.
   *
   * The refusals are the run model's, because they are facts about the RUN
   * (nothing playing, a step showing nothing, a step showing a picture) or
   * about the panel (no loaded element). What this owns is the wording, and
   * the distinction each refusal has to draw: the first three are permanent
   * for as long as the learner is where they are, so the Guide should say what
   * IS on screen and stop trying; the fourth is transient, so it should say so
   * and carry on.
   *
   * The reply carries the resulting POSITION rather than "done", because every
   * seek is clamped into the step's authored trim — a `back 15` three seconds
   * in lands at the start, and an agent that assumed otherwise would tell the
   * learner something that did not happen. It is the model's own reasoning, not
   * a line to read out: a success also carries {@link K_NARRATION_NONE}, which
   * ENDS the turn at the transport (`toolTurnHandover.ts`) so the command is
   * worked in silence. The learner asked for it, they are watching the panel,
   * and its scrubber is where the clamped position actually shows.
   *
   * ⚠ Only the `ok:` line is stamped. Every refusal below returns `error:`,
   * which the handover rejects outright — `media_not_ready` and the rest keep
   * their voice, because silence on a failure reads as a hang. The exception
   * is replaying or playing a video on a step with none (`no_video_here`): it
   * carries a fixed `reply="…"` line that Spark speaks for the model (AI-1459).
   */
  private controlMedia(args: ToolArgs): string {
    const action = getString(args, "action")?.trim().toLowerCase() ?? "";
    if (!action) {
      return "error: action_required (pass action=replay, back, forward, play or pause)";
    }

    const command = buildMediaCommand(
      action,
      getNumber(args, "seconds"),
      getString(args, "position"),
    );
    if (typeof command === "string") return command;

    // Read BEFORE the command: a refusal names the step the learner is on, and
    // `not_running` is the one case where there is nothing left to name.
    const stepName = this.m_runModel.getPlaybackView()?.stepName ?? "";
    const named = stepName ? `"${stepName}"` : "the current step";

    const result = this.m_runModel.controlStepMedia(command);
    // Asked to PLAY a video on a step that has none — an image, or a scene and
    // nothing else — the answer is one fixed line Spark speaks for the model
    // (user direction, 2026-10-05: "it should say 'there are no videos to replay
    // here'"; `toolTurnHandover.ts`). Pause, skip, seek and resize keep the
    // model's own answer below: "nothing to replay" is not what they asked.
    if (
      (result.outcome === "no_media" || result.outcome === "not_video")
      && (action === "replay" || action === "play")
    ) {
      return (
        `error: no_video_here${formatToolSpokenReply(`There are no videos to ${action} here.`)} ` +
        `(${named} has no video. Spark says that line for you and your turn ends here)`
      );
    }
    switch (result.outcome) {
      case "not_running":
        return (
          "error: workflow_not_running (there is no guided run with a current step, so there is " +
          "no step video to control)"
        );
      case "no_media":
        return (
          `error: step_has_no_media (${named} shows no image or video, so there is nothing to ` +
          "play - say what is actually on their screen rather than trying again)"
        );
      case "not_video":
        return (
          `error: step_media_not_video (${named} shows a still image, which has no playback. ` +
          "Nothing to replay, skip or pause)"
        );
      case "unavailable":
        return (
          `error: media_not_ready (${named} has a video but it is not loaded right now - it may ` +
          "still be fetching, or it failed to load. Nothing is stuck: tell them, and carry on)"
        );
      case "ok": {
        // The two ways a PRESENTATION command lands without changing anything.
        // Both report `ok` from the model's side — the video was commanded, the
        // panel simply has nowhere to move — so the wording is what tells the
        // agent to stop asking rather than to try again.
        if (result.state.isExpanded === null && (action === "expand" || action === "collapse")) {
          return (
            `error: media_not_resizable (${named} shows its video in a fixed panel on this ` +
            "display, with no full-size and small-card states to move between. Nothing is wrong: " +
            "the video is where it is going to be, so play it rather than resizing it)"
          );
        }
        if (action === "collapse" && result.state.isExpanded === true) {
          return (
            `error: media_is_the_step (${named} is a media-only step - the video IS the step, so ` +
            "there is no smaller card to put it back into. The way out is to move on)"
          );
        }
        return (
          `ok: media_control action=${action} ${describeMediaPosition(result.state)} ` +
          `${K_NARRATION_NONE} (the video is the outcome and the learner is watching it - ` +
          "your turn ends here, so say nothing about the command)"
        );
      }
    }
  }

  /**
   * `Toggle_Workflow_Outline` — the agent's half of the outline button that the
   * run header and the workflow editor both draw.
   *
   * Deliberately NOT gated on a run here or in the model: one flag drives the
   * outline in both modes, so an author asking for it while editing gets the
   * same panel the learner does — see `IWorkflowOutlineHost`.
   *
   * The two refusals draw the distinction the agent has to act on. No host is
   * "there is no workflow panel on this screen at all", which is permanent for
   * as long as they are where they are; an empty outline is a workflow with
   * nothing to list, where opening the panel would leave them reading a blank
   * one.
   */
  private toggleWorkflowOutline(args: ToolArgs): string {
    const visible = getBool(args, "visible");
    if (visible === null) {
      return "error: visible_required (pass visible=true to show the outline, false to hide it)";
    }

    const state = this.m_runModel.controlOutline(visible);
    if (state === null) {
      return (
        "error: workflow_panel_not_available (no workflow panel is on screen, so there is no " +
        "outline to show or hide)"
      );
    }
    if (visible && !state.isVisible) {
      return (
        "error: outline_empty (this workflow has no steps to list, so the outline would open " +
        "blank - say so rather than trying again)"
      );
    }
    return `ok: outline ${state.isVisible ? "shown" : "hidden"} entries=${state.entryCount}`;
  }

  /**
   * `stop_workflow` — the agent's half of the run header's Stop button.
   *
   * It presses that button rather than reimplementing it: one call to
   * `IWorkflowRunModel.requestStop`, which routes through the play-mode flag so
   * the editor shell, the tool palette and the Spark panel all come back the
   * same way they do for the button. In particular the EDITOR-vs-VIEWER split
   * is not decided here and must not be — `stop()` owns it, so the tool cannot
   * come to disagree with the button about who leaves the project.
   *
   * What it does own is REPORTING that split, because the two outcomes need
   * completely different things said next: a viewer is now looking at their
   * projects list, so an answer about the scene would be nonsense. The outcome
   * is OBSERVED rather than re-derived from permissions — a one-shot
   * subscription for the length of the call, which `Emitter.emit` makes exact
   * because the request is emitted synchronously from inside `stop()`. Asking
   * the permissions model instead would answer "would a viewer leave?", not
   * "did they" — and it would miss the case `requestProjectExitIfViewer`
   * returns early on, where there is no active project to leave.
   */
  private stopWorkflow(): string {
    // Read BEFORE the stop, while there is still a run to describe. Both are
    // null-safe when there is not, which is why there is no pre-check here:
    // "is there anything to stop" is `requestStop`'s rule, and restating it
    // would be a second copy of a predicate that must agree with it.
    const workflowName = this.m_runModel.getPlaybackView()?.workflowName ?? "";
    const stoppedOn = this.m_runModel.currentStepName;

    let exitedProject = false;
    const unsubscribe = this.m_runModel.onProjectExitRequested.on(() => {
      exitedProject = true;
    });
    let stopped: boolean;
    try {
      stopped = this.m_runModel.requestStop("the agent stopped the run");
    } finally {
      unsubscribe();
    }

    if (!stopped) {
      return "error: workflow_not_running (no guided run is in progress, so there is nothing to stop)";
    }

    const named = workflowName.trim() ? ` "${workflowName.trim()}"` : "";
    // The confirmation is FIXED, one line per outcome, and it is spoken FOR the
    // model rather than by it (`toolSpokenReply` + the orchestrator). So what
    // follows the field is for the conversation's record: what happened, and
    // that there is nothing left to say.
    if (exitedProject) {
      return (
        `ok: workflow_stopped${formatToolSpokenReply(K_WORKFLOW_STOPPED_REPLY)}${named} left_project=yes` +
        (stoppedOn ? ` stopped_on="${stoppedOn}"` : "") +
        ". The training is over and they are back at their projects list - this project is closed. " +
        "Spark has confirmed it with the reply line, word for word, so add nothing. You cannot reopen " +
        "the project or restart the run; they choose a project themselves."
      );
    }
    return (
      `ok: workflow_stopped${formatToolSpokenReply(K_PREVIEW_STOPPED_REPLY)}${named} left_project=no` +
      (stoppedOn ? ` stopped_on="${stoppedOn}"` : "") +
      ". Playback has closed and they are back in the project's editor, on its first scene. Spark has " +
      "confirmed it with the reply line, word for word, so add nothing. You are no longer guiding - do " +
      "not narrate the workflow, and do not start it again unless they ask."
    );
  }

  /**
   * `start_workflow` — the agent's half of the Play button, and
   * `stop_workflow`'s twin (AI-1357: an author saying "start preview").
   *
   * It presses the button rather than reimplementing it: one call to
   * `IWorkflowRunModel.requestStartAsync`, which flips the play-mode flag the
   * button flips and waits for the start that flag runs — so the editor shell,
   * the role handover and every refusal are the button's, and the tool cannot
   * come to disagree with it about what Play means. Whether the workflow CAN
   * play is the model's `canStart`, never restated here.
   *
   * What it does own is the WORDING, one job per outcome. A refusal has to say
   * what is in the way — no workflow, an unreadable file, the validation
   * errors — so the agent can offer the fix in the same turn. A success says
   * nothing the agent should act on, because the result ENDS its turn
   * (`toolTurnHandover.ts`): by the time it is posted the run has taken the
   * session over as the Guide, which reads it later as history.
   *
   * Two awaits before the press, both so the run plays what the author sees:
   * the editor's debounced keystrokes, and the document itself, which may still
   * be loading when "start preview" is the first thing said in a project.
   */
  private async startWorkflowAsync(): Promise<string> {
    try {
      await this.m_workflowModel.flushPendingEditsAsync();
    } catch (error) {
      // A preview of what was last saved beats no preview at all.
      console.warn("[AI: AgentToolExecutor] StartWorkflow could not flush pending edits:", error);
    }

    let document: WorkflowDocument | null;
    try {
      document = await this.m_workflowModel.getWorkflowAsync();
    } catch (error) {
      console.warn("[AI: AgentToolExecutor] StartWorkflow failed to load the workflow:", error);
      return "error: workflow_load_failed";
    }

    const outcome = await this.m_runModel.requestStartAsync("the agent started the run");
    const view = this.m_runModel.getPlaybackView();
    const workflowName = (view?.workflowName ?? document?.workflowName ?? "").trim();
    const named = workflowName ? ` "${workflowName}"` : "";

    switch (outcome) {
      case "started":
        return (
          `ok: workflow_started${named}` +
          (view ? ` current="${view.stepName}" position=${view.overallIndex}/${view.overallCount}` : "") +
          ". It is playing exactly as if Play had been pressed: the Guide has taken over and the " +
          "run's steps narrate themselves, so the turn that started it ends with this call."
        );
      case "awaiting_console_placement":
        return (
          `ok: workflow_awaiting_console_placement${named}. The workflow first asks the wearer where ` +
          "the stage goes: console placement is open on their headset, and the run starts as soon as " +
          "they finish or cancel it. The turn that started it ends with this call."
        );
      case "already_running": {
        const current = this.m_runModel.currentStepName;
        return (
          `error: workflow_already_running${named} (a run is already playing` +
          (current ? `, on "${current}"` : "") +
          " - there is nothing to start. stop_workflow ends it, if that is what they want)"
        );
      }
      case "not_playable":
        return this.describeUnplayableWorkflow(document);
      case "not_started":
        return (
          "error: workflow_start_failed (the run did not start - the workflow changed, or stopped " +
          "being playable, while it was loading. Try once more; if it still does not start, say so " +
          "and suggest the Play button)"
        );
    }
  }

  /**
   * Why `canStart` refused, in terms the agent can act on in the same turn.
   *
   * The DECISION is not re-made here — the run model has already refused — it
   * is explained from the facts that decision read: whether a document exists,
   * whether the stored file was unreadable, and the report's errors. A handful
   * of errors are quoted rather than all of them, because the agent reads them
   * out loud and the full report is one `get_workflow` away.
   */
  private describeUnplayableWorkflow(document: WorkflowDocument | null): string {
    if (!document) {
      if (this.m_workflowModel.loadError) {
        return (
          "error: workflow_unreadable (the project's workflow file cannot be read, so nothing can " +
          "play until it is repaired. Repairing it is the Workflow Builder's job - get_workflow " +
          "returns the broken file and every issue in it)"
        );
      }
      return (
        "error: no_workflow_for_project (this project has no workflow yet, so there is nothing to " +
        "preview. The Workflow Builder creates one, or the user can with the '+' on the Workflow pill)"
      );
    }

    const errors = this.m_workflowModel.validateLoadedWorkflow().errors;
    if (errors.length === 0) {
      return "error: workflow_not_playable (the workflow has no step that can play)";
    }
    const listed = errors.slice(0, K_MAX_LISTED_START_ERRORS).join("; ");
    const more =
      errors.length > K_MAX_LISTED_START_ERRORS
        ? `; and ${errors.length - K_MAX_LISTED_START_ERRORS} more`
        : "";
    return (
      `error: workflow_not_playable errors=${errors.length} (a workflow plays only once its ` +
      `validation report has no errors: ${listed}${more}. Say what is in the way; fixing it is the ` +
      "Workflow Builder's job, and the Play button stays disabled until it is done)"
    );
  }

  /**
   * `go_to_workflow_step` — the agent's half of the learner's Back / Next.
   *
   * Everything it does, the Workflow panel's own buttons already do, and it goes
   * through the SAME run-model calls rather than its own sequencing, so the two
   * cannot come to disagree about what a move means: Next COMPLETES the step,
   * while Back and a jump navigate without completing it.
   *
   * `checked` moves pass the step-move guardrail first (AI-1412): each branch
   * runs its own structural refusals, then asks whether the learner's last turn
   * asked for the move, then moves. A replay is never checked (user direction,
   * 2026-09-28) — it does not change the step.
   */
  private async goToWorkflowStepAsync(args: ToolArgs, checked: boolean): Promise<string> {
    if (!this.m_runModel.isRunning) {
      return "error: workflow_not_running (no guided run is in progress - play mode starts one)";
    }

    const triggerLabel = getString(args, "trigger")?.trim() ?? "";
    const stepName = getString(args, "step")?.trim() ?? "";
    const direction = getString(args, "direction")?.trim().toLowerCase() ?? "";
    const back = getNumber(args, "back");
    if (!triggerLabel && !stepName && !direction && back === null) {
      return (
        "error: step_or_direction_required (pass direction=next, direction=replay, back=<n>, " +
        "step=<step name>, or trigger=<button label>)"
      );
    }

    // Precedence is the one the tool description states: a BUTTON on the step
    // in front of the learner is the most specific thing the model can say,
    // then a step name, then a count, then a direction. A model that sends two
    // has told us the same intent twice, not two intents — and `trigger` above
    // `direction=next` is what saves the commonest confusion of the four, a
    // spoken answer sent as both the button and "move them on".
    if (triggerLabel) return this.pressWorkflowTriggerAsync(triggerLabel, checked);
    if (stepName) return this.jumpToWorkflowStepAsync(stepName, checked);
    if (back !== null) return this.goBackWorkflowStepsAsync(back, checked);

    if (direction === "previous") return this.goBackWorkflowStepsAsync(1, checked);

    // "Do this step again" — replay, redo, repeat — carries NO step name, and
    // that is the point of it: the model does not have to work out which step
    // "this" is. Measured 2026-09-11: asked to replay the step after two outline
    // picks, it re-sent its own last named jump, 80 s old, over run-state
    // bullets that correctly named the step the learner was on.
    if (direction === "replay") return this.replayWorkflowStep();

    if (direction !== "next") {
      return `error: unknown_direction "${direction}" (expected next, previous or replay)`;
    }

    const before = this.m_runModel.getPlaybackView();

    // A BRANCH POINT the author wrote with no Next: the learner's only way on
    // is one of the trigger buttons, and which one is their answer to the
    // step's question. `advanceToNextStep` does not know the button is absent —
    // it completes the step and walks written order — so without this the
    // agent could take a learner past a choice their own panel will not let
    // them skip, into whichever branch happens to be written next. It is the
    // same move-without-a-choice the prompt forbids for `trigger=`, and it is
    // the one a model told "do not guess a button" reaches for instead, so it
    // is closed here rather than in prose (2026-09-23). Scoped to steps WITH
    // buttons: a step that has neither is a dead end the validator already
    // reports, and nothing here makes it better or worse.
    if (before !== null && before.nextButtonLabel === null && before.triggers.length > 0) {
      return (
        `error: no_next_button (this step has no Next button - its buttons ${this.describeTriggers(before.triggers)} ` +
        "are the only way on, and which one is the learner's answer to give. Nothing moved. Ask them the " +
        "step's question again, naming the buttons, and press the one their answer names with trigger=; " +
        "never choose one for them.)"
      );
    }

    // Next on the LAST step completes the workflow and stays there — the run
    // does not end itself — so report completion rather than a move that the
    // position line would show as not having happened.
    const wasOnLastStep = before !== null && !before.hasNext;

    // The step's `completion_condition` holds Next for the agent exactly as it
    // does for the learner's button — same call, same gate. Checked HERE as
    // well so the refusal can say what is being waited for: `advanceToNextStep`
    // answers with a bare false, and reporting that as "no current step" would
    // send the model looking for a run that is in fact playing normally.
    if (before !== null && before.isNextBlocked) {
      return (
        `error: step_not_ready (${before.nextBlockedReason ?? "this step is not finished yet"}). ` +
        "The learner's Next button is disabled for the same reason, so nothing is stuck - say what " +
        "they are waiting for and try again once it is over."
      );
    }

    const unasked = await this.refuseUnaskedMoveAsync(
      checked,
      {
        kind: "next",
        vocabulary: {
          stepNames: this.nextStepNames(),
          buttonLabels: [],
          moveLabels: before?.nextButtonLabel ? [before.nextButtonLabel] : [],
        },
      },
      "move them on to the next step",
      // On the step Next finishes on there is nothing to move on TO.
      before !== null && (wasOnLastStep || before.isCompletionStep)
        ? "Ready to finish?"
        : "Ready to move on?",
    );
    if (unasked) return unasked;

    if (!this.m_runModel.advanceToNextStep({ by: "agent", via: "next" })) {
      return "error: workflow_step_unavailable (the run has no current step)";
    }

    // Whether the run ENDED is asked of the model afterwards rather than
    // predicted from the position beforehand, because an authored ending
    // (`is_completion_step`) can sit anywhere: on such a step `hasNext` is
    // still true while Next finishes the workflow, so predicting would report
    // a move that did not happen. There is no position left to describe.
    if (!this.m_runModel.isRunning) {
      return (
        "ok: workflow_step run_finished (that step was the workflow's authored ending, so the run " +
        "is over and playback has closed - tell the learner it is finished; only they can start it again)"
      );
    }
    return this.describeRunPosition(wasOnLastStep ? "workflow_complete" : "moved");
  }

  /**
   * A named jump goes BACK over ground the learner has covered, never ahead.
   *
   * The run is a sequence they are being taken through, so the agent must not be
   * able to drop them into a step they have not reached — that skips the
   * preparation the workflow was written to give them, and it is not something
   * they can ask for by mistake in conversation the way they can ask for a step
   * name they half-remember. Forward movement stays exactly one step at a time
   * through `direction=next`, which COMPLETES the step it leaves.
   *
   * Branching forward remains the AUTHOR's decision — which is why `trigger=`
   * is not a hole in this rule and this one is not made redundant by it. That
   * call presses a button the author wrote onto the step in front of the
   * learner; this one would let the agent name any destination it liked. The
   * refusal below routes between them when the named step is in fact one of
   * those buttons' targets.
   *
   * The refusals name the covered steps, because that is what lets the model
   * recover in one turn — the learner describes a step rather than naming it, so
   * a near-miss on the name is the normal case, not an error.
   */
  /**
   * "Let's go back three steps." Counts MOVES along the learner's path, not
   * positions in the document — the two disagree the moment a trigger branches
   * or repeats a step, which is exactly when the learner is most likely to ask.
   *
   * A count past the start is refused rather than clamped to the first step:
   * "back 5" on a 3-deep path is a misunderstanding of where they are, and
   * silently landing them at the beginning hides it. The refusal carries the
   * real depth AND the path, so the model can offer the furthest real move in
   * the same turn instead of guessing a second number.
   */
  private async goBackWorkflowStepsAsync(count: number, checked: boolean): Promise<string> {
    const depth = Math.max(0, this.m_runModel.visitStack.length - 1);
    if (!Number.isInteger(count) || count < 1) {
      return (
        `error: invalid_back "${count}" (back takes a whole number of steps, 1 or more; ` +
        `${this.describePath()})`
      );
    }
    if (depth === 0) {
      return `error: no_previous_step (the run is still on the first step of the learner's path)`;
    }
    if (count > depth) {
      return (
        `error: back_too_far (the learner has only walked ${depth} step${depth === 1 ? "" : "s"} ` +
        `from where they started - ${this.describePath()}). Offer them the furthest real move ` +
        "instead of guessing another number."
      );
    }

    const stack = this.m_runModel.visitStack;
    const landing = stack[stack.length - 1 - count];
    const backLabel = this.m_runModel.getPlaybackView()?.backButtonLabel ?? null;
    const unasked = await this.refuseUnaskedMoveAsync(
      checked,
      {
        kind: "back",
        vocabulary: {
          stepNames: landing ? [landing] : [],
          buttonLabels: [],
          moveLabels: backLabel ? [backLabel] : [],
        },
      },
      `take them back ${count} step${count === 1 ? "" : "s"}`,
      count === 1 ? "Want to go back a step?" : `Want to go back ${count} steps?`,
    );
    if (unasked) return unasked;

    if (!this.m_runModel.goBackSteps(count, { by: "agent", via: "back" })) {
      return `error: workflow_step_unavailable (the run could not go back ${count} steps)`;
    }
    return this.describeRunPosition("moved");
  }

  private async jumpToWorkflowStepAsync(stepName: string, checked: boolean): Promise<string> {
    const document = this.m_workflowModel.loadedWorkflow;
    const target = document?.findStep(stepName) ?? null;
    if (!target) {
      return (
        `error: unknown_step "${stepName}" (${this.describeCoveredSteps()}; ` +
        "call get_workflow for the exact step names and pick the one the learner described)"
      );
    }

    if (isStepDisabled(target)) {
      return (
        `error: step_disabled "${target.name}" (that step is disabled, so it is not part of the ` +
        "run at all and there is no way to reach it - the learner has never seen it). Pick a step " +
        `that is playing (${this.describeCoveredSteps()}), or tell the author it is disabled.`
      );
    }

    // The step they are ALREADY on: "show me that again". The step is replayed
    // — its video back to the start, its on-start script played again — rather
    // than the call answering "already there" and doing nothing. That no-op is
    // what sent the model reaching for play_workflow_script instead, which
    // plays a script outside its step: no media reset, no gate, wrong order.
    // `direction=replay` is the call the Guide is taught for this; a name that
    // happens to be the current step lands in the same place.
    if (this.m_runModel.currentStepName?.toLowerCase() === target.name.toLowerCase()) {
      return this.replayWorkflowStep();
    }

    if (!this.m_runModel.hasVisitedStep(target.name)) {
      // A forward name that one of THIS step's buttons already points at is
      // the near-miss worth catching: the learner answered the step's question
      // out loud, and the model reached for the destination rather than the
      // control. Naming `trigger=` here is what recovers it in one turn —
      // without it the refusal reads as "you cannot go there", which is only
      // true of a jump the author never authorised.
      const button = this.findTriggerToward(target.name);
      if (button) {
        return (
          `error: step_not_reached_yet "${target.name}" (a named jump only goes back over ground ` +
          `the learner has covered) - but this step has a "${button.label}" button pointing there, ` +
          `so press it with trigger="${button.label}" instead. That is the author's own branch and ` +
          "it takes them where you were aiming."
        );
      }
      return (
        `error: step_not_reached_yet "${target.name}" (a named jump only goes back over ` +
        `ground the learner has covered - ${this.describeCoveredSteps()}). Use direction=next ` +
        "to move the run forward one step at a time, or tell them what still comes first."
      );
    }

    const unasked = await this.refuseUnaskedMoveAsync(
      checked,
      { kind: "step", vocabulary: { stepNames: [target.name], buttonLabels: [], moveLabels: [] } },
      `take them back to "${target.name}"`,
      `Want to go back to ${target.name}?`,
    );
    if (unasked) return unasked;

    if (!this.m_runModel.goBackToStep(target.name, { by: "agent", via: "step" })) {
      return `error: workflow_step_unavailable (the run could not move to "${target.name}")`;
    }
    return this.describeRunPosition("moved");
  }

  /**
   * `trigger=<label>` — the learner SAID one of the step's buttons instead of
   * clicking it (AI-1352). Spark is a conversational guide, and a step whose
   * question is answered with buttons had no spoken answer at all: `step=` is
   * refused for a branch target they have not reached, and `direction=next`
   * both completes the step and walks document order rather than the branch —
   * on the ticket's own step, which authors no Next, it is not even an exit.
   *
   * It does NOT widen where the agent may send them. A named jump stays
   * backwards-only because the agent choosing a forward destination skips the
   * preparation the workflow was written to give; this presses a control the
   * AUTHOR put on the step the learner is already looking at, which is the same
   * branch their click takes and the same one the panel offers. The limit it
   * keeps is that there is nothing here to choose: no button, no move.
   *
   * Matched against the PLAYBACK VIEW's triggers, not the document's, for three
   * reasons that all have to hold: its labels are token-RESOLVED, so they are
   * the words the learner actually read and said; its list is already filtered
   * to the buttons that are rendered (a malformed one, or one pointing at a
   * parked step, is not on screen and must not be pressable by voice when it is
   * not pressable by hand); and its `index` is the source-list position
   * `activateTrigger` dispatches on.
   */
  private async pressWorkflowTriggerAsync(label: string, checked: boolean): Promise<string> {
    const view = this.m_runModel.getPlaybackView();
    const triggers = view?.triggers ?? [];
    if (triggers.length === 0) {
      // Names Next by the AUTHOR's label, which can be any wording — and one
      // that reads like an answer ("Area is clear") is exactly what a model
      // mistakes for a trigger and tries to press here.
      const next = view?.nextButtonLabel ?? null;
      return (
        `error: no_trigger_buttons (the step the learner is on${view ? ` ("${view.stepName}")` : ""} ` +
        "has no trigger buttons, so there is nothing to press" +
        (next !== null
          ? `; its only way on is its Next button, which reads "${next.replace(/"/g, "'")}". ` +
            "If they are ready to move on, that is direction=next.)"
          : ".)")
      );
    }

    // Case-insensitive exact, the same lookup a step name gets
    // (`WorkflowDocument.findStep`). Deliberately not fuzzy: the labels are
    // listed verbatim in the run-state bullets, so resolving what the learner
    // MEANT ("yeah, all clear" -> "Area is clear") is the model's job over that
    // list, and a near-match here would press a button nobody chose.
    const wanted = label.trim().toLowerCase();
    const matches = triggers.filter((trigger) => trigger.label.trim().toLowerCase() === wanted);
    if (matches.length === 0) {
      // The author can label Next anything, so the label passed may be Next's.
      const next = view?.nextButtonLabel ?? null;
      if (next !== null && next.trim().toLowerCase() === wanted) {
        return (
          `error: unknown_trigger "${label}" (that is this step's Next button, not a trigger - ` +
          "pressing it is direction=next.)"
        );
      }
      return (
        `error: unknown_trigger "${label}" (this step's buttons are ${this.describeTriggers(triggers)}). ` +
        "Pass one label exactly as it is written - it is what the learner is looking at, so pick the " +
        "one their words mean rather than passing their words."
      );
    }

    // Duplicate labels are legal (`validate()` only warns), so the press has to
    // say which. Identical targets are not a real ambiguity — either button
    // lands in the same place — so only DIFFERENT ones are refused, and the
    // refusal goes back to the learner rather than guessing on their behalf.
    const distinctTargets = new Set(matches.map((trigger) => trigger.target.toLowerCase()));
    if (distinctTargets.size > 1) {
      return (
        `error: ambiguous_trigger "${label}" (this step has ${matches.length} buttons labelled that, ` +
        `going to ${matches.map((trigger) => `"${trigger.target}"`).join(" and ")} - pressing by name ` +
        "would be a guess). Ask the learner which one they mean and let them press it, and tell the " +
        "author the labels are duplicated."
      );
    }

    const pressed = matches[0]!;
    // Any of the step's buttons, and anywhere they lead: which one the learner
    // meant is the model's call over the labels, and the check only asks
    // whether they answered at all.
    const document = this.m_workflowModel.loadedWorkflow;
    const unasked = await this.refuseUnaskedMoveAsync(
      checked,
      {
        kind: "trigger",
        vocabulary: {
          stepNames: triggers.map((trigger) =>
            document ? (findPlayableNavigationTarget(document, trigger.target)?.name ?? trigger.target) : trigger.target,
          ),
          buttonLabels: triggers.map((trigger) => trigger.label),
          // A trigger press is matched on the answer, not on "the button that
          // makes this move" — every trigger label is already in `buttonLabels`.
          moveLabels: [],
        },
      },
      `press "${pressed.label}"`,
      null,
    );
    if (unasked) return unasked;

    if (!this.m_runModel.activateTrigger(pressed.index, "agent")) {
      return `error: workflow_step_unavailable (the "${pressed.label}" button could not navigate)`;
    }
    return this.describeRunPosition("branched");
  }

  /**
   * The step-move guardrail's answer as a refusal to return, or null to go
   * ahead with the move (AI-1412, `AIGuardrailsModel.StepMove.ts`).
   *
   * `move` finishes the sentence "ask you to …" in the refusal, so the model is
   * told exactly which move did not happen. `question` is the one line Spark
   * asks to confirm it, so the learner hears the same words every time.
   *
   * ⚠ The check can WAIT — up to a few seconds for a spoken turn's transcript —
   * and the learner can press Next, Back or a button in that time. So an
   * allowed move goes ahead only if the run is still on the step it was
   * checked against; otherwise the agent's move would land on top of theirs.
   */
  private async refuseUnaskedMoveAsync(
    checked: boolean,
    request: StepMoveRequest,
    move: string,
    question: string | null,
  ): Promise<string | null> {
    if (!checked || !this.m_stepMoves) return null;
    const stepBefore = this.m_runModel.currentStepName;
    const verdict = await this.m_stepMoves.checkAsync(request);

    if (verdict.allowed) {
      if (this.m_runModel.isRunning && this.m_runModel.currentStepName === stepBefore) return null;
      return (
        "error: run_moved_meanwhile (the learner moved the run, or ended it, while this move was " +
        "being checked, so nothing more moved - " +
        (this.m_runModel.isRunning
          ? `they are on "${this.m_runModel.currentStepName ?? "no step"}" now`
          : "the run is over") +
        ")"
      );
    }

    const heard = verdict.heard.replace(/"/g, "'").trim();
    // How the confirmation is to be ASKED: the caller's own question, word for
    // word. Two rounds got here.
    //   - AI-1418: "ask them in one short line" was read as licence to coach —
    //     "say 'next' when you're ready" — on a step whose button reads "Start".
    //     The shape became a yes/no QUESTION, which is also what lets a bare
    //     "yes" confirm it (the guardrail counts one only after a question,
    //     `endsWithQuestion`), and the question had to name the button.
    //   - 2026-10-08: naming the button became "Do you want me to press Next
    //     to continue?" — Spark offering to press it, in different words each
    //     time. The user chose one neutral line that leaves every way on open:
    //     press the button, say its label, or answer "yes" / "continue". The
    //     label still counts as an answer (`moveLabels`), so it no longer has
    //     to be in the question.
    // A trigger press passes no question and gets the bare shape: its answer
    // is one of the step's buttons, which no single line can ask about.
    const confirm =
      "ask them ONE short yes/no question" +
      (question ? `, "${question.replace(/"/g, "'")}",` : "") +
      " and move only once they say yes. Never tell them which words to say.";
    switch (verdict.reason) {
      case "noise":
        // Stamped so the transport ends the turn: nobody asked anything, and a
        // spoken refusal is Spark answering background noise. `toolTurnHandover`.
        return (
          `error: ${K_MOVE_NOT_REQUESTED} ${K_NARRATION_NONE} heard="${heard}" (nothing moved: the ` +
          "learner's last turn was a sound, not words, so it asked for nothing. Your turn ends here " +
          "with nothing said.)"
        );
      case "not_asked":
        if (request.kind === "trigger") {
          return (
            `error: ${K_MOVE_NOT_REQUESTED} heard="${heard}" (nothing moved: those words do not answer ` +
            "the step's question, so no button is pressed. Reply to what they actually said, then ask " +
            "the step's question again in one short line, naming the buttons.)"
          );
        }
        return (
          `error: ${K_MOVE_NOT_REQUESTED} heard="${heard}" (nothing moved: those words do not ask you ` +
          `to ${move}. Reply to what they actually said. If you think they want you to ${move}, ` +
          `${confirm})`
        );
      case "unheard":
        return (
          `error: ${K_MOVE_NOT_REQUESTED} heard="${heard}" (nothing moved: what the learner last said ` +
          `could not be made out, so nothing shows they asked you to ${move}. To find out, ${confirm})`
        );
      case "no_turn":
        return (
          `error: ${K_MOVE_NOT_REQUESTED} (nothing moved: the learner has not said anything asking ` +
          `you to ${move}. Moving the run is theirs to ask for.)`
        );
    }
  }

  /**
   * The step `direction=next` would land on, in written order over the playable
   * steps — the same walk `WorkflowRunModel.advancePastCurrentStep` takes. Empty
   * on the last step, where Next finishes rather than moves.
   */
  private nextStepNames(): string[] {
    const current = this.m_runModel.currentStepName;
    const steps = getPlayableSteps(this.m_workflowModel.loadedWorkflow);
    const index = current ? steps.findIndex((step) => equalsIgnoreCase(step.name, current)) : -1;
    const next = index >= 0 ? steps[index + 1] : undefined;
    return next ? [next.name] : [];
  }

  /**
   * A button on the CURRENT step that lands on `stepName`, or null. Resolves
   * each button's `go_to` the way a press does — a group target lands on its
   * first playable step — so a trigger pointing at a group still matches the
   * step the model named.
   */
  private findTriggerToward(stepName: string): WorkflowPlaybackTriggerView | null {
    const document = this.m_workflowModel.loadedWorkflow;
    if (!document) return null;
    return (
      this.m_runModel
        .getPlaybackView()
        ?.triggers.find((trigger) =>
          equalsIgnoreCase(
            findPlayableNavigationTarget(document, trigger.target)?.name ?? "",
            stepName,
          ),
        ) ?? null
    );
  }

  /** The current step's buttons as `"label" -> target`, for a refusal to recover from. */
  private describeTriggers(triggers: ReadonlyArray<WorkflowPlaybackTriggerView>): string {
    return triggers.map((trigger) => `"${trigger.label}" -> ${trigger.target}`).join(", ");
  }

  /**
   * Plays the step the learner is on again from the top — see
   * `IWorkflowRunModel.replayCurrentStep`. Reached by `direction=replay` and by
   * naming the current step; neither moves the learner along their path.
   */
  private replayWorkflowStep(): string {
    const current = this.m_runModel.currentStepName;
    if (!current) return "error: workflow_step_unavailable (the run has no current step)";
    if (!this.m_runModel.replayCurrentStep({ by: "agent", via: "replay" })) {
      return `error: workflow_step_unavailable (the run could not replay "${current}")`;
    }
    return this.describeRunPosition("replayed");
  }

  private describeCoveredSteps(): string {
    const covered = this.m_runModel.visitedStepNames;
    if (covered.length === 0) return "the run has covered no steps yet";
    return `steps reached so far: ${covered.map((name) => `"${name}"`).join(", ")}`;
  }

  /** The learner's route, numbered, so a refusal can be recovered from in one turn. */
  private describePath(): string {
    const path = this.m_runModel.visitStack;
    if (path.length === 0) return "the run has walked no path yet";
    return `their path so far: ${path.map((name, i) => `${i + 1}. "${name}"`).join(", ")}`;
  }

  private describeRunPosition(
    outcome: "moved" | "replayed" | "branched" | "workflow_complete",
  ): string {
    const view = this.m_runModel.getPlaybackView();
    if (!view) return `ok: workflow_step ${outcome}`;
    const group = view.groupName ? ` group="${view.groupName}"` : "";
    const position =
      `ok: workflow_step ${outcome} current="${view.stepName}" ` +
      `position=${view.overallIndex}/${view.overallCount}${group}`;
    // Next on the last step moved nothing: the run stays put for the guide to
    // wrap up, so this one keeps its voice and carries no narration field
    // (`toolHandedOverSpokenTurn` ends the turn on either value).
    if (outcome === "workflow_complete") {
      return `${position} (they have finished the workflow's last step - tell them the training is complete)`;
    }
    // The buttons the learner has NOW, in the conversation right after the
    // move. The run-state bullets say the same every turn, but the model weighs
    // its own last tool reply over them: after pressing a trigger it went on
    // treating the NEXT step's confirmation as a button choice, with bullets
    // saying the step had none (2026-10-01 trace). Next by the AUTHOR's label,
    // which can be any words. Neither field contains `narration=`, which
    // `toolHandedOverSpokenTurn` matches by substring.
    const next = view.nextButtonLabel === null ? "none" : `"${view.nextButtonLabel.replace(/"/g, "'")}"`;
    const triggers =
      view.triggers.length === 0
        ? "none"
        : view.triggers.map((trigger) => `"${trigger.label.replace(/"/g, "'")}"`).join(",");
    const buttons = ` next=${next} triggers=${triggers}`;
    // Every other success ENDS the turn — see `toolTurnHandover.ts`. The field
    // still says which case it is, because the model reads it as well.
    //
    // ⚠ `none` carries NO explanation. It used to say "this step says nothing
    // out loud - its instructions are on the learner's panel", and the Guide
    // handed that back to a learner who asked "What?" (2026-09-29). What to
    // SAY on such a step is the run-state bullet's job, not this line's.
    return this.hasArrivedOnScriptNarratedStep(view)
      ? `${position}${buttons} ${K_NARRATION_SCRIPT} (this step's own on-start script is speaking now - your ` +
          "turn ends here and anything you would have added is dropped, so do not narrate over it)"
      : `${position}${buttons} ${K_NARRATION_NONE} (your turn ends here)`;
  }

  /**
   * Whether the step just arrived at narrates ITSELF. Both answers end the
   * model's turn now (2026-09-28, see `toolTurnHandover.ts`); this decides only
   * what the result TELLS the model — that the author's lines are playing, or
   * that nothing is said out loud on this step.
   *
   * Two facts, composed here rather than added to the run model, because each
   * already exists and neither means this on its own:
   *
   *   - `isOnStartScriptHoldingFloor` is the LIVE half. It is armed —
   *     synchronously, inside the arrival, before the run announces it —
   *     exactly when the arrived step has an on-start script still to play, and
   *     released on any terminal outcome. So it answers "is that script
   *     speaking right now", which a static look at the document cannot.
   *
   *     ⚠ NOT `isAiInputHeld`, which it used to read: that field is the
   *     COMPOSER's decision and is now gated by a flag that ships OFF, so it
   *     answers no on every step — the Guide would narrate over every scripted
   *     arrival. The two were one field until 2026-09-01 and only ever agreed
   *     by coincidence.
   *   - `stepOnStartScriptSpeaks` is the NARROW half: a script of nothing but
   *     tool steps holds the composer and says nothing, so it reports
   *     `narration=none` rather than claiming a script is speaking.
   */
  private hasArrivedOnScriptNarratedStep(view: WorkflowPlaybackView): boolean {
    if (!view.isOnStartScriptHoldingFloor) return false;
    const step = this.m_workflowModel.loadedWorkflow?.findStep(view.stepName) ?? null;
    return step !== null && stepOnStartScriptSpeaks(step);
  }
}

function formatWorkflowYamlIssue(issue: WorkflowYamlIssue, index: number): string {
  const location =
    issue.line === null
      ? ""
      : `Line ${issue.line}${issue.column === null ? "" : `, column ${issue.column}`}: `;
  return `${index + 1}. ${location}${issue.message}`;
}

/** How far a skip moves when the agent names no distance — the panel's own step. */
const K_DEFAULT_MEDIA_SKIP_SECONDS = 15;

/**
 * How many validation errors a refused `start_workflow` quotes. The agent says
 * them out loud, and the whole report is one `get_workflow` away.
 */
const K_MAX_LISTED_START_ERRORS = 3;

/**
 * The two fixed confirmations of a stop, spoken in the model's place — see
 * `toolSpokenReply`. Verbatim user direction (AI-1357): an author previewing
 * hears the first and stays in the project; a learner who cannot edit it
 * hears the second on the way back to their projects list. "Nothing more", so
 * no punctuation is added either.
 */
const K_PREVIEW_STOPPED_REPLY = "Preview stopped";
const K_WORKFLOW_STOPPED_REPLY = "Workflow stopped";

/**
 * The `action` / `seconds` pair as a command, or the refusal to send back.
 *
 * A non-positive `seconds` is REFUSED rather than negated: "back -10" is the
 * model having misunderstood the direction, and silently flipping it would hide
 * that. The direction is the action's, never the number's.
 */
function buildMediaCommand(
  action: string,
  seconds: number | null,
  position: string | null,
): WorkflowMediaCommand | string {
  switch (action) {
    case "replay":
      return { kind: "replay" };
    case "play":
      return { kind: "play" };
    case "pause":
      return { kind: "pause" };
    case "stop":
      return { kind: "stop" };
    case "expand":
      return { kind: "expand" };
    case "collapse":
      return { kind: "collapse" };
    case "back":
    case "forward": {
      const magnitude = seconds ?? K_DEFAULT_MEDIA_SKIP_SECONDS;
      if (!Number.isFinite(magnitude) || magnitude <= 0) {
        return (
          `error: invalid_seconds "${seconds}" (seconds is how FAR to skip, so it must be a ` +
          `positive number; the direction is action=${action}. Omit it for ` +
          `${K_DEFAULT_MEDIA_SKIP_SECONDS}s)`
        );
      }
      return { kind: "seek", deltaSeconds: action === "back" ? -magnitude : magnitude };
    }
    case "seek": {
      // Parsed by the format's OWN timecode reader, the one an author's
      // `media.start` / `media.end` goes through — so "1:30" means the same
      // thing in a tool call as it does in the file, and there is one place
      // that decides what a timecode is.
      const raw = position?.trim() ?? "";
      if (!raw) {
        return (
          "error: position_required (action=seek needs position - a point in the step's clip, " +
          'like "1:30" or a number of seconds. Use action=back / action=forward to move by a ' +
          "distance instead)"
        );
      }
      const parsed = parseMediaTimecodeSeconds(raw);
      if (parsed === null) {
        return (
          `error: invalid_position "${raw}" (expected seconds, mm:ss or hh:mm:ss, counted from ` +
          "the step's own clip start)"
        );
      }
      return { kind: "seek_to", positionSeconds: parsed };
    }
    default:
      return (
        `error: unknown_action "${action}" (expected play, pause, stop, replay, back, forward, ` +
        "seek, expand or collapse)"
      );
  }
}

/**
 * Where the video ended up, written the way the learner sees it — and the
 * clip's own frame, so a trimmed video reads `0:04/0:20` of its section rather
 * than of the source file. The length is omitted rather than guessed while it
 * is still unknown.
 */
function describeMediaPosition(state: WorkflowMediaPlaybackState): string {
  const position = formatMediaTimecode(state.positionSeconds);
  const clip =
    state.durationSeconds === null
      ? position
      : `${position}/${formatMediaTimecode(state.durationSeconds)}`;
  // The presentation is reported only where the surface HAS one: null means an
  // XR slate, which places its panel in metres, and saying `expanded=no` there
  // would invite an expand that can never happen.
  const expanded =
    state.isExpanded === null ? "" : ` expanded=${state.isExpanded ? "yes" : "no"}`;
  return `position=${clip} playing=${state.isPlaying ? "yes" : "no"}${expanded}`;
}

/** One vector as the inline YAML mapping a `step_view` takes, at mm precision. */
function formatVec3(v: Vec3): string {
  const [x, y, z] = v.map((n) => Math.round(n * 1000) / 1000);
  return `{ x: ${x}, y: ${y}, z: ${z} }`;
}
